// DOM & navigation utilities — extracted from MyScripts/Utils.ts

import { hasValue, hasStringValue } from './validation';
import { App } from '@/services/app-compat';

import { notifyApp } from '@/utils/window-globals';

export function getRelativeUrlForMapLocation(address: string): string | null {
  const moduleType: string = App.getModuleTypeFromURL();
  const uriEncodedAddress = encodeURIComponent(encodeURIComponent(address));

  if (moduleType === "cad") {
    return `/?sectionName=CAD&tab=Map&function=NPSD_MapByAddress&multiModule=true&params=${uriEncodedAddress}`;
  } else if (moduleType === "mdt") {
    return `/?sectionName=MDT&tab=Map&function=NPSD_MapByAddress&multiModule=true&params=${uriEncodedAddress}`;
  }
  return null;
}

// A saved call's map must open by call id, not by address text: the by-id screen fetches the
// call and centres on its stored coordinate, while the by-address screen re-derives the
// location on every render.
export function getRelativeUrlForMapByCallCardId(callCardId: number | string): string | null {
  const moduleType: string = App.getModuleTypeFromURL();

  if (moduleType === "cad") {
    return `/?sectionName=CAD&tab=Map&function=CAD_MapById&multiModule=true&params=${callCardId}`;
  } else if (moduleType === "mdt") {
    return `/?sectionName=MDT&tab=Map&function=MDT_MapById&multiModule=true&params=${callCardId}`;
  }
  return null;
}

export function openPage(href: string, newWindow: boolean = false): void {
  var link = document.createElement("a");
  link.href = href;
  if (newWindow) link.target = "_blank";

  var evt = new MouseEvent("click", {
    'view': window,
    'bubbles': true,
    'cancelable': false,
  });
  link.dispatchEvent(evt);
}

export function openPageInNewWindow(href: string): void {
  openPage(href, true);
}

export function openWebpage(href: string): void {
  const w = window as any;
  if (w.isUWPApp || w.isIOSApp || w.isAndroidApp || w.isWindowsAppSdkApp) {
    notifyApp("externalLink", [href]);
  } else {
    window.open(href, '_blank');
  }
}

export function setButtonEnabled(buttonId: string, isEnabled: boolean): void {
  const el = document.getElementById(buttonId) as HTMLButtonElement;
  if (el) el.disabled = !isEnabled;
}

// Same image list as the backend (OrnaAttachmentUtils.isImageFromFilename): these get resized thumbnails.
export function hasImageExtension(filename: string): boolean {
  let matched = filename.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i);
  if (!hasValue(matched)) {
    return false;
  }
  return (matched.length > 0);
}

export function isBasePageCaseReport(): boolean {
  const href = window.location.href;
  return href.indexOf("tab=CaseReports") > -1 || href.indexOf("/case-report") > -1;
}

export function isBasePageCallCard(): boolean {
  const href = window.location.href;
  return href.indexOf("pageName=CallCard.html") > -1 || href.indexOf("tab=CallCard") > -1 || href.indexOf("/call-card") > -1;
}

export function isBasePageCitation(): boolean {
  const href = window.location.href;
  return href.indexOf("pageName=CitationForm.html") > -1 || href.indexOf("tab=Citations") > -1 || href.indexOf("/citation") > -1;
}

export function isBasePageCallCardOrCaseReport(): boolean {
  return isBasePageCallCard() || isBasePageCaseReport();
}

export function getAppOrBrowserString(): string {
  const w = window as any;
  return ((w.isUWPApp || w.isIOSApp || w.isAndroidApp || w.isWindowsAppSdkApp) ? "app" : "browser");
}

export function scrollToElementOnMobile(
  element: HTMLElement,
  offset: number = 60,
  mobileBreakpoint: number = 1024,
): void {
  if (window.innerWidth <= mobileBreakpoint) {
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset + rect.top - offset;
    window.scrollTo({ top: scrollTop, behavior: 'smooth' });
  }
}

export function scrollToLastMobileCard(
  containerEl: HTMLElement,
  selector: string = '.mobile-cards .card',
  offset: number = 60,
  mobileBreakpoint: number = 1024,
): void {
  const cards = containerEl.querySelectorAll(selector);
  const lastCard = cards[cards.length - 1] as HTMLElement;
  if (lastCard) {
    scrollToElementOnMobile(lastCard, offset, mobileBreakpoint);
  }
}

export function showHomePage(): void {
  if (App.isViewingMDTPage()) {
    window.OrnaReact.navigate.toModuleHome('MDT');
  } else if (App.isViewingCADPage()) {
    window.OrnaReact.navigate.toModuleHome('CAD');
  } else if (App.isViewingAdminPage()) {
    window.OrnaReact.navigate.toModuleHome('ADMIN');
  }
}

export function refreshPage(): void {
  window.OrnaReact.navigate.refresh();
}

/**
 * Execute a callback after two `requestAnimationFrame` ticks.
 *
 * A "double rAF" ensures the browser has had a full
 * layout/paint cycle after a React/DOM update, so that
 * conditionally rendered elements (e.g. modal inputs) are
 * actually present in the DOM before we try to interact
 * with them.
 */
export function afterPaint(callback: () => void): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
}

/**
 * Focus a React ref's current element after the next paint cycle.
 *
 * Typical usage inside a `useEffect` that fires when a modal opens:
 *
 *   useEffect(() => {
 *     if (open) focusAfterPaint(inputRef);
 *   }, [open]);
 */
export function focusAfterPaint(ref: React.RefObject<HTMLElement | null>): void {
  afterPaint(() => {
    ref.current?.focus();
  });
}
